import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, Shield, Crown, Loader2, ArrowRight, Clock, CheckCircle2,
  AlertCircle, ExternalLink, Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import { useBilling } from "../context/BillingContext";

/**
 * Billing — plan picker + status, Stripe-hosted checkout + customer portal.
 *
 * Routes land here from:
 *   • Trial banner CTA
 *   • Any 402 response (frontend writes rejected by soft-lock)
 *   • Settings → Billing link
 */
export default function BillingPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { billing, refetch } = useBilling();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  // Toast on checkout return
  useEffect(() => {
    const result = params.get("checkout");
    if (result === "success") {
      toast.success("Payment confirmed — your shop is unlocked");
      refetch();
      // Strip the query param so a refresh doesn't re-toast
      setParams({}, { replace: true });
    } else if (result === "cancelled") {
      toast.info("Checkout cancelled — pick a plan when you're ready");
      setParams({}, { replace: true });
    }
  }, [params, refetch, setParams]);

  // Load plans
  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data || [])).catch(() => setPlans([]));
  }, []);

  const checkout = async (planKey) => {
    setBusy(planKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan:       planKey,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Couldn't start checkout — please try again");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy("");
    }
  };

  const portal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      if (data?.portal_url) {
        window.location.href = data.portal_url;
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't open billing portal");
    } finally {
      setBusy("");
    }
  };

  const activePlan = billing?.plan;
  const subbed = !!billing?.stripe_subscription_id;

  return (
    <div className="min-h-screen bg-black text-white px-6 py-8 md:py-12" data-testid="billing-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <div className="text-label flex items-center gap-2 text-amber-300">
            <Sparkles className="w-3 h-3" /> Billing
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tighter mt-2">
            Plans &amp; billing
          </h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            All plans include unlimited leads, quotes, and jobs. Upgrade or cancel any time from the
            customer portal — your card is on file with Stripe, we never touch it.
          </p>
        </div>

        {/* Current status card */}
        {billing && <CurrentStatusCard billing={billing} onManage={portal} subbed={subbed} busy={busy === "portal"} />}

        {/* Plans */}
        <div className="mt-10">
          <div className="text-label mb-3">Pick a plan</div>
          <div className="grid md:grid-cols-2 gap-4">
            {plans.map((p) => (
              <PlanCard
                key={p.key}
                plan={p}
                active={activePlan === p.key && billing?.status === "active"}
                busy={busy === p.key}
                onPick={() => checkout(p.key)}
              />
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-3">
            Secure payment via Stripe — card details never touch our servers. You'll be redirected to
            Stripe Checkout and returned here when you're done.
          </p>
        </div>
      </div>
    </div>
  );
}

function CurrentStatusCard({ billing, onManage, subbed, busy }) {
  const { status, plan_name, trial_days_left, locked, current_period_ends_at } = billing;

  let tone, label, line1, line2;
  if (locked && status === "past_due") {
    tone = "danger"; label = "Past due";
    line1 = `Your last ${plan_name} payment failed.`;
    line2 = "Update your card in the portal to unlock writes again.";
  } else if (locked && status === "canceled") {
    tone = "danger"; label = "Cancelled";
    line1 = "Your subscription has been cancelled.";
    line2 = "Pick a plan below to reactivate your shop.";
  } else if (locked) {
    tone = "danger"; label = "Trial expired";
    line1 = "Your 14-day free trial has ended.";
    line2 = "Pick a plan below to keep making changes.";
  } else if (status === "trialing") {
    tone = "warn"; label = `${trial_days_left ?? 0} day${trial_days_left === 1 ? "" : "s"} left`;
    line1 = "You're on the free trial.";
    line2 = "No card required until the trial ends — add a plan below any time.";
  } else if (status === "active") {
    tone = "success"; label = "Active";
    line1 = `You're on the ${plan_name} plan.`;
    line2 = current_period_ends_at
      ? `Next renewal: ${new Date(current_period_ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}.`
      : "Your subscription is in good standing.";
  } else {
    tone = "muted"; label = status || "Unknown";
    line1 = `Current plan: ${plan_name}.`;
    line2 = "";
  }

  const palette = {
    trial:   "border-amber-500/30 bg-amber-500/10",
    warn:    "border-amber-500/30 bg-amber-500/10",
    danger:  "border-red-500/30 bg-red-500/10",
    success: "border-emerald-500/30 bg-emerald-500/10",
    muted:   "border-white/10 bg-white/5",
  }[tone];
  const Icon = tone === "danger" ? AlertCircle : tone === "success" ? CheckCircle2 : Clock;

  return (
    <div className={`rounded-2xl border p-5 ${palette}`} data-testid="billing-status-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Status</div>
            <div className="font-heading text-xl text-white">{label}</div>
            <div className="text-sm text-slate-200 mt-1">{line1}</div>
            {line2 && <div className="text-xs text-slate-400 mt-0.5">{line2}</div>}
          </div>
        </div>
        {subbed && (
          <button
            data-testid="billing-manage-btn"
            onClick={onManage}
            disabled={busy}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 text-xs font-semibold transition disabled:opacity-50 shrink-0"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
            Manage billing
          </button>
        )}
      </div>
    </div>
  );
}

function PlanCard({ plan, active, busy, onPick }) {
  const isPro = plan.key === "pro";
  const Icon = isPro ? Crown : Shield;
  return (
    <div
      data-testid={`plan-card-${plan.key}`}
      className={`relative rounded-3xl p-6 border transition ${
        active
          ? "border-emerald-500/60 bg-emerald-500/10"
          : "border-white/10 bg-white/[0.02] hover:border-white/30"
      }`}
    >
      {isPro && !active && (
        <span className="absolute -top-2 right-5 bg-emerald-400 text-black text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full">
          Most popular
        </span>
      )}
      {active && (
        <span className="absolute -top-2 right-5 bg-emerald-500 text-black text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full">
          Current plan
        </span>
      )}
      <div className="flex items-center gap-2">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${isPro ? "border-amber-400/40 bg-amber-400/10 text-amber-200" : "border-white/10 bg-black/40 text-slate-200"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-heading text-2xl text-white leading-none">{plan.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Monthly subscription</div>
        </div>
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-heading text-4xl text-white">${plan.price_aud}</span>
        <span className="text-xs text-slate-400 uppercase tracking-widest">AUD / mo</span>
      </div>
      <ul className="mt-4 space-y-2 text-sm text-slate-200">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        data-testid={`plan-pick-${plan.key}`}
        onClick={onPick}
        disabled={busy || active}
        className={`mt-5 w-full flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold transition disabled:opacity-50 ${
          active
            ? "bg-emerald-500/20 text-emerald-200 cursor-default"
            : "bg-white text-black hover:bg-slate-100"
        }`}
      >
        {busy ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe…</>
        ) : active ? (
          <>Current plan</>
        ) : (
          <>Choose {plan.name} <ArrowRight className="w-4 h-4" /></>
        )}
      </button>
    </div>
  );
}
